import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import config from '../config';
import authService from '../services/auth';
import { Helmet } from 'react-helmet-async';

const Section = styled.div`
    position: relative;
    background-color: ${props => props.$backgroundColor || 'transparent'};
    opacity: ${props => props.$opacity || 1};
    margin-bottom: 2rem;
`;

const ContentRow = styled.div`
    width: 100%;
    display: flex;
    flex-direction: row;
    align-items: center;

    @media (max-width: 768px) {
        flex-direction: column !important;
    }
`;

const ImageContainer = styled.div`
    width: 50%;
    overflow: hidden;
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;

    img {
        width: 50%;
        height: auto;
        object-fit: contain;
        display: block;
    }

    @media (max-width: 768px) {
        width: 100%;
        height: auto;
        min-height: 40lvh;

        img {
            width: 60%;
        }
    }
`;

const TextContainer = styled.div`
    width: 50%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 2rem 1rem;

    h2 {
        font-size: 2rem;
        margin-bottom: 1.5rem !important;
        font-weight: 700;
        color: var(--nody-black);
        line-height: 1.2;
    }

    @media (max-width: 768px) {
        width: 100%;
        padding: 2rem 1rem;
        height: auto;

        h2 {
            font-size: 2rem !important;
            text-align: left;
        }
    }
`;

const Description = styled.div`
    font-size: 1rem;
    line-height: 1.5;
    text-align: left;
    color: var(--nody-black-75) !important;

    p {
        color: var(--nody-black-75) !important;
    }

    ul {
        padding-left: 1.5rem !important;
    }

    @media (max-width: 768px) {
        text-align: left;
    }
`;

// Navigazione laterale con pallini
const Navigation = styled.div`
    position: fixed;
    right: 20px;
    top: 40vh;
    z-index: 1000;
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 10px;
    pointer-events: auto;

    @media (max-width: 768px) {
        right: 10px;
    }
`;

const NavDot = styled.div`
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: rgba(0, 0, 0, 0.5);
    cursor: pointer;
    transition: all 0.2s ease;
    position: relative;

    &:hover {
        background-color: rgba(0, 0, 0, 0.5);
    }

    &.active {
        background-color: rgba(0, 0, 0, 0.8);
        transform: scale(2);
        box-shadow: 0 0 4px rgba(0, 0, 0, 0.2);
    }

    &:after {
        content: '';
        position: absolute;
        width: 24px;
        height: 24px;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        border-radius: 50%;
    }

    @media (max-width: 768px) {
        width: 4px;
        height: 4px;
        
        &.active {
            transform: scale(2.5);
        }
    }
`;

function Extra() {
    const [pageContent, setPageContent] = useState(null);
    const [sections, setSections] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedLang] = useState(localStorage.getItem('language') || config.languages.default);
    const [currentSectionIndex, setCurrentSectionIndex] = useState(0);

    useEffect(() => {
        const fetchSectionDetails = async (sectionId) => {
            try {
                const response = await authService.get(config.API_ENDPOINTS.getParagraph(sectionId, selectedLang));
                return response;
            } catch (error) {
                console.error(`Errore durante il recupero della sezione ${sectionId}:`, error);
                return null;
            }
        };

        const fetchPageContent = async () => {
            try {
                const response = await authService.get(config.API_ENDPOINTS.getPageList(selectedLang));
                const extraPage = response.find(page => page.field_tipologia_pagina?.[0]?.value === 'extra' && page.langcode?.[0]?.value === selectedLang);

                if (extraPage && extraPage.field_sezioni) {
                    setPageContent(extraPage);

                    const sectionsDetails = await Promise.all(
                        extraPage.field_sezioni.map(section =>
                            fetchSectionDetails(section.target_id)
                        )
                    );

                    setSections(sectionsDetails.filter(Boolean));
                }

                setLoading(false);
            } catch (error) {
                console.error('Errore durante il recupero dei contenuti:', error);
                setLoading(false);
            }
        };

        fetchPageContent();
    }, [selectedLang]);

    useEffect(() => {
        // Funzione per gestire lo scroll e aggiornare l'indice della sezione corrente
        const handleScroll = () => {
            // Utilizziamo l'80% dell'altezza della finestra, così il cambio avviene quasi subito
            // quando la sezione comincia ad apparire nella parte bassa della finestra
            const scrollPosition = window.scrollY + (window.innerHeight * 0.8);
            
            // Trova quale sezione è attualmente visibile
            for (let i = 0; i < sections.length; i++) {
                const current = document.getElementById(`section-${i}`);
                if (current) {
                    const sectionTop = current.offsetTop;
                    const sectionBottom = sectionTop + current.offsetHeight;
                    
                    if (scrollPosition >= sectionTop && scrollPosition < sectionBottom) {
                        setCurrentSectionIndex(i);
                        break;
                    }
                }
            }
        };
        
        // Aggiungi event listener per lo scroll
        window.addEventListener('scroll', handleScroll);
        // Chiamalo una volta per impostare l'indice iniziale
        handleScroll();
        
        // Pulizia event listener
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [sections]);

    // Funzione per scorrere alla sezione selezionata
    const scrollToSection = (index) => {
        const sectionId = `section-${index}`;
        const section = document.getElementById(sectionId);
        if (section) {
            window.scrollTo({
                top: section.offsetTop,
                behavior: 'smooth'
            });
        }
    };

    // Durante il caricamento mostro solo una pagina vuota
    if (loading) {
        return null;
    }

    // Se non ci sono contenuti, mostro anche pagina vuota
    if (!pageContent || sections.length === 0) {
        return null;
    }

    return (
        <main>
            <div className="container">
                {sections.map((section, index) => {
                    const sectionId = `section-${index}`;

                    return (
                        <Section
                            key={section.id.value}
                            id={sectionId}
                            $backgroundColor={section.field_colore_sezione?.color}
                            $opacity={section.field_colore_sezione?.opacity}
                        >
                            <ContentRow>
                                {section.field_immagine_sezione && (
                                    <ImageContainer>
                                        <img
                                            src={section.field_immagine_sezione.url}
                                            alt={section.field_immagine_sezione.alt || "Immagine sezione"}
                                        />
                                    </ImageContainer>
                                )}

                                <TextContainer>
                                    {section.field_titolo_sezione && (
                                        <h2>{section.field_titolo_sezione.value}</h2>
                                    )}
                                    {section.field_testo_sezione && (
                                        <Description
                                            dangerouslySetInnerHTML={{
                                                __html: section.field_testo_sezione.value
                                            }}
                                        />
                                    )}
                                </TextContainer>
                            </ContentRow>
                        </Section>
                    );
                })}
                
                {/* Mostra l'indicatore solo se ci sono più di una sezione */}
                {sections.length > 1 && (
                    <Navigation>
                        {sections.map((_, index) => (
                            <NavDot 
                                key={index}
                                className={currentSectionIndex === index ? 'active' : ''}
                                onClick={() => scrollToSection(index)}
                            />
                        ))}
                    </Navigation>
                )}
            </div>
        </main>
    );
}

export default Extra; 